import React, { useMemo } from "react";
import { X, ArrowRight } from "lucide-react";
import { fmtCurrency, fmtDate } from "../lib/formatters";

// Slice E2 — Element change delta popup
// Shows the from → to log captured on an auto-created scenario, grouped by
// change type (Cost, Quantity, Useful Life When New, Remaining Useful Life).
//
// Data comes off `scenario.element_changes` (array of
// `{element_id, element_name, field, from, to}`). Server writes this in
// PATCH /associations/{aid}/elements/batch when any of the 4 tracked key
// fields change.

const FIELD_META = {
  current_cost:          { label: "Cost / UoM",              format: (v) => fmtCurrency(v),   order: 1 },
  quantity:              { label: "Quantity",                format: (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }), order: 2 },
  useful_life_when_new:  { label: "Useful Life When New",    format: (v) => `${Math.round(v)} yrs`, order: 3 },
  remaining_useful_life: { label: "Remaining Useful Life",   format: (v) => `${Math.round(v)} yrs`, order: 4 },
};

export default function ElementChangesModal({ scenario, onClose }) {
  const changes = scenario?.element_changes || [];

  const grouped = useMemo(() => {
    const m = new Map();
    for (const c of changes) {
      // Server ships from_value/to_value (Slice E2 storage format).
      const from = c.from_value !== undefined ? c.from_value : c.from;
      const to = c.to_value !== undefined ? c.to_value : c.to;
      if (!m.has(c.field)) m.set(c.field, []);
      m.get(c.field).push({ ...c, from, to });
    }
    return Array.from(m.entries())
      .sort((a, b) => (FIELD_META[a[0]]?.order || 99) - (FIELD_META[b[0]]?.order || 99))
      .map(([field, items]) => ({
        field,
        items: items.sort((a, b) => (a.element_name || "").localeCompare(b.element_name || "")),
      }));
  }, [changes]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      onClick={onClose}
      data-testid="element-changes-modal"
    >
      <div
        className="bg-white border-2 border-navy shadow-2xl w-[720px] max-w-[95vw] max-h-[85vh] flex flex-col rounded-[8px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-rule bg-navy-wash">
          <div>
            <div className="text-[13px] font-semibold text-navy uppercase tracking-[0.08em]" data-testid="ecm-title">
              Element Changes Captured
            </div>
            <div className="text-[10.5px] text-slate mt-0.5">
              <span className="font-semibold text-ink">{scenario.name}</span>
              {scenario.created_at && <> · {fmtDate(scenario.created_at)}</>}
              {" · "}
              <span className="font-mono tabular-nums text-navy font-semibold">
                {changes.length}
              </span>{" "}
              change{changes.length === 1 ? "" : "s"} across{" "}
              <span className="font-mono tabular-nums text-navy font-semibold">
                {new Set(changes.map((c) => c.element_id)).size}
              </span>{" "}
              element{new Set(changes.map((c) => c.element_id)).size === 1 ? "" : "s"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate hover:text-coral"
            data-testid="ecm-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="overflow-auto p-3 flex-1">
          {grouped.length === 0 && (
            <div className="text-center text-slate py-8 text-[12px]">
              No element changes were captured with this scenario.
            </div>
          )}

          {grouped.length > 0 && (
            <table className="u-table u-table-dense w-full">
              {/* Single header row at the top per Dave 2026-02-17.
                  Column headings right-aligned to match numeric columns. */}
              <thead>
                <tr>
                  <th style={{ background: "var(--u-navy-wash)", color: "var(--u-navy)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10.5, textAlign: "right" }}>
                    Element
                  </th>
                  <th style={{ background: "var(--u-navy-wash)", width: 130, color: "var(--u-navy)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10.5, textAlign: "right" }}>
                    From
                  </th>
                  <th style={{ background: "var(--u-navy-wash)", width: 22 }}></th>
                  <th style={{ background: "var(--u-navy-wash)", width: 130, color: "var(--u-navy)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10.5, textAlign: "right" }}>
                    To
                  </th>
                  <th style={{ background: "var(--u-navy-wash)", width: 110, color: "var(--u-navy)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10.5, textAlign: "right" }}>
                    Change
                  </th>
                </tr>
              </thead>
              <tbody>
                {grouped.map((g) => {
                  const meta = FIELD_META[g.field] || { label: g.field, format: (v) => v };
                  return (
                    <React.Fragment key={g.field}>
                      {/* Group section header row — capitalized, navy bold */}
                      <tr data-testid={`ecm-group-${g.field}`}>
                        <td
                          colSpan={5}
                          style={{
                            background: "var(--u-navy)",
                            color: "#fff",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.12em",
                            fontSize: 10.5,
                            padding: "4px 8px",
                          }}
                        >
                          {(meta.label + " CHANGES").toUpperCase()}
                          <span className="ml-2 font-mono tabular-nums text-[10px] font-normal opacity-75">
                            {g.items.length} row{g.items.length === 1 ? "" : "s"}
                          </span>
                        </td>
                      </tr>
                      {g.items.map((c, i) => {
                        const delta = (c.to || 0) - (c.from || 0);
                        const positive = delta > 0;
                        return (
                          <tr key={`${c.element_id}-${i}`}>
                            <td className="text-ink font-semibold" style={{ textTransform: "uppercase" }}>
                              {c.element_name}
                            </td>
                            <td className="num text-slate">{meta.format(c.from)}</td>
                            <td className="text-center text-slate"><ArrowRight size={11} /></td>
                            <td className="num text-ink font-semibold">{meta.format(c.to)}</td>
                            <td
                              className="num"
                              style={{
                                color: positive ? "#EA6A47" : "#00838F",
                                fontWeight: 600,
                              }}
                            >
                              {positive ? "+" : ""}
                              {meta.format(delta)}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-end px-4 py-2 border-t border-rule bg-navy-wash">
          <button
            onClick={onClose}
            className="bg-navy text-white hover:bg-[#12406B] px-3 py-1 text-[12px] font-semibold"
            data-testid="ecm-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
